import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, Typography } from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { ActionsButtons } from 'components/CustomButtons/ActionsButtons';
import { ModalV2 } from 'components/Modal/ModalV2';
import AccountingAccountForm from './AccountingAccountForm';
import { selectChildAccounts } from './accountingAccountSelectors';
import { useRuleValidator } from 'hooks/useRuleValidator';
import { useToggle } from 'hooks/useToggle';
import { PERMISSIONS } from 'router/permissions';
import { deleteAccountingAccount } from 'redux/actions/accountingAccount/accountingAccountActions';
import { ACCOUNTING_ACCOUNT_LOADINGS } from 'redux/reducers/accountingAccount/accountingAccountTypes';

const { CATALOGS_ACCOUNTING_ACCOUNTS_EDIT, CATALOGS_ACCOUNTING_ACCOUNTS_DELETE } = PERMISSIONS;

const ActionsCell = ({ original }) => {
  const dispatch = useDispatch();
  const isLoadingComp = useSelector(({ search }) => search.isLoadingComp);
  const childAccounts = useSelector((state) => selectChildAccounts(state, original.id));
  const canEdit = useRuleValidator(CATALOGS_ACCOUNTING_ACCOUNTS_EDIT);
  const canDelete = useRuleValidator(CATALOGS_ACCOUNTING_ACCOUNTS_DELETE);
  const [isUpdateFormOpen, setIsUpdateFormOpen] = useToggle();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useToggle();
  const [isChildrenModalOpen, setIsChildrenModalOpen] = useToggle();

  const loading = isLoadingComp?.[ACCOUNTING_ACCOUNT_LOADINGS.DELETE] || false;

  const hasChildren = childAccounts.length > 0;

  const handleDelete = () => {
    dispatch(deleteAccountingAccount(original.id, setIsDeleteModalOpen));
  };

  // Una cuenta con subcuentas no puede eliminarse (dejaría cuentas hijas
  // apuntando a un padre inexistente). Si las tiene, se muestra un modal
  // informativo con los números de las subcuentas en lugar de confirmar.
  const handleDeleteClick = () => {
    if (hasChildren) {
      setIsChildrenModalOpen();
      return;
    }
    setIsDeleteModalOpen();
  };

  const buttons = [
    {
      Icon: EditIcon,
      color: 0,
      onClick: setIsUpdateFormOpen,
      tooltip: "Modificar cuenta contable",
      validation: canEdit,
    },
    {
      Icon: DeleteIcon,
      color: 3,
      onClick: handleDeleteClick,
      tooltip: "Eliminar cuenta contable",
      validation: canDelete,
    }
  ];

  return (
    <>
      <ActionsButtons buttons={buttons} />

      {isUpdateFormOpen && (
        <AccountingAccountForm
          data={original}
          open={isUpdateFormOpen}
          setOpen={setIsUpdateFormOpen}
          titlePrefix="Actualizar"
          update
        />
      )}
      {isDeleteModalOpen && (
        <ModalV2
          title="¿Seguro que desea eliminar esta cuenta contable?"
          confirmText="Eliminar"
          open={isDeleteModalOpen}
          onClose={setIsDeleteModalOpen}
          onSubmit={handleDelete}
          justConfirm
          isLoading={loading}
          confirmColor="warning"
        />
      )}
      {isChildrenModalOpen && (
        <ModalV2
          title="No se puede eliminar esta cuenta contable"
          open={isChildrenModalOpen}
          onClose={setIsChildrenModalOpen}
        >
          <Grid item xs={12}>
            <Typography variant="body1" style={{ marginBottom: 16 }}>
              La cuenta tiene las siguientes subcuentas asociadas. Elimine o
              reasigne dichas subcuentas antes de continuar:
            </Typography>
            {childAccounts.map((acc) => (
              <Typography key={acc.id} variant="body2" style={{ fontWeight: 600 }}>
                {acc.number}
              </Typography>
            ))}
          </Grid>
        </ModalV2>
      )}
    </>
  );
};

export default ActionsCell;
